import React from 'react';
import { bool, func, node, oneOf, shape, string } from 'prop-types';

import Typography from '@/components/Typography';
import { ALERT_TYPES, DEFAULT_ALERT_ICONS, POSITIONS } from './constants';
import { getAlertStyles, StyledAlert, StyledAlertContainer } from './style';

/**
 * @typedef {{
 * action: {text: string, onClick: Function },
 * dataTestId: string,
 * show: boolean,
 * icon: React.ReactNode,
 * text: string,
 * type: 'default' | 'info' | 'success' | 'caution' | 'error'
 * }} AlertProps
 * @type {React.FC<AlertProps>}
 * @returns
 */
const Alert = props => {
  const { action, dataTestId, icon, show, text, type, ...rest } = props;

  const renderIcon = () => {
    if (icon) {
      return (
        <icon.type
          size={20}
          color={getAlertStyles(type).icon}
          {...icon.props}
        />
      );
    }
    if (['success', 'caution', 'error'].includes(type)) {
      const Component = DEFAULT_ALERT_ICONS[type];
      return <Component size={20} color={getAlertStyles(type).icon} />;
    }
    return null;
  };

  return (
    <StyledAlertContainer {...rest}>
      <StyledAlert role="alert" type={type} show={show} {...rest}>
        {renderIcon()}

        <Typography type="body" margin={0} data-testid="txtAlert">
          {text}
        </Typography>

        {action?.onClick && action?.text && (
          <Typography
            type="alert"
            alertType="link"
            onClick={action.onClick}
            style={{ display: 'inline', marginTop: 0, marginLeft: 8 }}
            data-testid={`btnAction-${dataTestId}`}
          >
            {action.text}
          </Typography>
        )}
      </StyledAlert>
    </StyledAlertContainer>
  );
};

Alert.propTypes = {
  action: shape({
    text: string,
    onClick: func,
  }),
  dataTestId: string,
  icon: node,
  show: bool,
  text: string,
  type: oneOf(ALERT_TYPES),
  position: oneOf(POSITIONS),
};

Alert.defaultProps = {
  action: {},
  dataTestId: 'divAlert',
  icon: null,
  show: false,
  text: '',
  type: 'error',
  position: 'right',
};

export default Alert;
